/**
 * AuditLogDetailView Component
 *
 * Displays detailed view of a single audit log entry
 * Shows before/after comparison with diff highlighting
 *
 * Related Story: WORKFORCE-V2-story-2.004
 */

import React, { useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Card, CardContent } from '@/components/ui/Card'
import { X, Clock, User, Activity, Globe, Monitor } from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { AuditLogEntry } from '@/lib/services/audit-log-api'
import { cn } from '@/lib/utils'

interface AuditLogDetailViewProps {
  log: AuditLogEntry
  isOpen: boolean
  onClose: () => void
}

interface DiffEntry {
  field: string
  before?: any
  after?: any
  status: 'added' | 'removed' | 'changed' | 'unchanged'
}

export function AuditLogDetailView({ log, isOpen, onClose }: AuditLogDetailViewProps) {
  /**
   * Parse JSON values safely
   */
  const parseJsonSafely = (jsonString?: string): Record<string, any> | null => {
    if (!jsonString) return null

    try {
      return JSON.parse(jsonString)
    } catch {
      return null
    }
  }

  /**
   * Compute diff between before and after values
   */
  const diffData = useMemo<DiffEntry[]>(() => {
    const beforeValue = parseJsonSafely(log.beforeValue)
    const afterValue = parseJsonSafely(log.afterValue)

    if (!beforeValue && !afterValue) {
      return []
    }

    // CREATE operation - only afterValue
    if (!beforeValue && afterValue) {
      return Object.entries(afterValue).map(([field, value]) => ({
        field,
        after: value,
        status: 'added' as const
      }))
    }

    // DELETE operation - only beforeValue
    if (beforeValue && !afterValue) {
      return Object.entries(beforeValue).map(([field, value]) => ({
        field,
        before: value,
        status: 'removed' as const
      }))
    }

    // UPDATE operation - compare before and after
    const allFields = new Set([
      ...Object.keys(beforeValue || {}),
      ...Object.keys(afterValue || {})
    ])

    return Array.from(allFields).map(field => {
      const before = beforeValue?.[field]
      const after = afterValue?.[field]

      if (before === undefined && after !== undefined) {
        return { field, after, status: 'added' as const }
      }

      if (before !== undefined && after === undefined) {
        return { field, before, status: 'removed' as const }
      }

      if (JSON.stringify(before) !== JSON.stringify(after)) {
        return { field, before, after, status: 'changed' as const }
      }

      return { field, before, after, status: 'unchanged' as const }
    })
  }, [log.beforeValue, log.afterValue])

  /**
   * Format timestamp
   */
  const formattedTimestamp = format(
    new Date(log.timestamp),
    "dd/MM/yyyy 'às' HH:mm:ss",
    { locale: ptBR }
  )

  /**
   * Get entity type badge color
   */
  const getEntityTypeBadgeColor = (entityType: string) => {
    switch (entityType) {
      case 'EMPLOYEE':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/40'
      case 'PAYROLL':
        return 'bg-green-500/20 text-green-400 border-green-500/40'
      case 'DEPARTMENT':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/40'
      case 'ROLE':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/40'
      default:
        return 'bg-brand-gray/20 text-brand-gray border-brand-gray/40'
    }
  }

  /**
   * Format value for display
   */
  const formatValue = (value: any): string => {
    if (value === null || value === undefined) {
      return 'null'
    }

    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2)
    }

    return String(value)
  }

  /**
   * Get diff status styling
   */
  const getDiffStatusStyle = (status: DiffEntry['status']) => {
    switch (status) {
      case 'added':
        return 'bg-green-500/10 border-green-500/40'
      case 'removed':
        return 'bg-red-500/10 border-red-500/40'
      case 'changed':
        return 'bg-yellow-500/10 border-yellow-500/40'
      case 'unchanged':
        return 'bg-brand-gray/5 border-brand-gray/20'
    }
  }

  /**
   * Get diff status label
   */
  const getDiffStatusLabel = (status: DiffEntry['status']) => {
    switch (status) {
      case 'added':
        return 'Adicionado'
      case 'removed':
        return 'Removido'
      case 'changed':
        return 'Modificado'
      case 'unchanged':
        return 'Inalterado'
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-brand-dark-light border-brand-gray/20">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold text-white flex items-center gap-3">
            <Activity className="w-6 h-6 text-brand-primary" />
            Detalhes do Log de Auditoria
          </DialogTitle>

          <DialogClose asChild>
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-4 top-4"
              onClick={onClose}
            >
              <X className="w-4 h-4" />
            </Button>
          </DialogClose>
        </DialogHeader>

        <div className="mt-6 space-y-6">
          {/* Metadata Section */}
          <Card>
            <CardContent className="p-6">
              <h3 className="font-display text-lg font-semibold text-white mb-4">
                Informações do Log
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-brand-primary mt-0.5" />
                  <div>
                    <p className="text-sm text-brand-gray">Timestamp</p>
                    <p className="text-white font-mono">{formattedTimestamp}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-brand-primary mt-0.5" />
                  <div>
                    <p className="text-sm text-brand-gray">Usuário</p>
                    <p className="text-white">{log.userName}</p>
                    <p className="text-xs text-brand-gray font-mono">{log.userId}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Activity className="w-5 h-5 text-brand-primary mt-0.5" />
                  <div>
                    <p className="text-sm text-brand-gray mb-1">Tipo de Entidade</p>
                    <Badge className={getEntityTypeBadgeColor(log.entityType)}>
                      {log.entityType}
                    </Badge>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Activity className="w-5 h-5 text-brand-primary mt-0.5" />
                  <div>
                    <p className="text-sm text-brand-gray">Operação</p>
                    <p className="text-white font-medium">{log.operation}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Activity className="w-5 h-5 text-brand-primary mt-0.5" />
                  <div>
                    <p className="text-sm text-brand-gray">ID da Entidade</p>
                    <p className="text-white font-mono">{log.entityId}</p>
                  </div>
                </div>

                {log.ipAddress && (
                  <div className="flex items-start gap-3">
                    <Globe className="w-5 h-5 text-brand-primary mt-0.5" />
                    <div>
                      <p className="text-sm text-brand-gray">Endereço IP</p>
                      <p className="text-white font-mono">{log.ipAddress}</p>
                    </div>
                  </div>
                )}

                {log.userAgent && (
                  <div className="flex items-start gap-3 md:col-span-2">
                    <Monitor className="w-5 h-5 text-brand-primary mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-brand-gray">User Agent</p>
                      <p className="text-white text-sm break-all">{log.userAgent}</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Diff Section */}
          {diffData.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <h3 className="font-display text-lg font-semibold text-white mb-4">
                  Comparação de Valores
                </h3>

                <div className="space-y-3">
                  {diffData.map((entry, index) => (
                    <div
                      key={index}
                      className={cn(
                        'p-4 rounded-lg border',
                        getDiffStatusStyle(entry.status)
                      )}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-white">{entry.field}</span>
                        <Badge className="text-xs">
                          {getDiffStatusLabel(entry.status)}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                        {/* Before Value */}
                        {entry.before !== undefined && (
                          <div>
                            <p className="text-xs text-brand-gray mb-1 font-semibold">Antes</p>
                            <pre className="bg-brand-dark p-3 rounded text-sm text-red-300 overflow-x-auto">
                              {formatValue(entry.before)}
                            </pre>
                          </div>
                        )}

                        {/* After Value */}
                        {entry.after !== undefined && (
                          <div>
                            <p className="text-xs text-brand-gray mb-1 font-semibold">Depois</p>
                            <pre className="bg-brand-dark p-3 rounded text-sm text-green-300 overflow-x-auto">
                              {formatValue(entry.after)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* No Changes */}
          {diffData.length === 0 && (
            <Card>
              <CardContent className="p-6 text-center">
                <p className="text-brand-gray">
                  Nenhuma alteração detectada neste log.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
